import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, DownloadButton } from '@devkit/ui';
import { Package } from 'lucide-react';
import { trackToolExecute, trackCopy, trackDownload, trackClear } from '@devkit/analytics';

const defaultJson = `{
  "name": "DevKit",
  "version": "1.0.0",
  "description": "Fast, private developer tools",
  "author": "DevKit Team"
}`;

export default function JsonMinifierPage() {
  const [input, setInput] = useState(defaultJson);
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const minify = useCallback(() => {
    setError(null);
    try {
      const parsed = JSON.parse(input);
      setOutput(JSON.stringify(parsed));
      trackToolExecute('json-minifier', 'minify');
    } catch (e) {
      setError((e as Error).message);
      setOutput('');
    }
  }, [input]);

  const clearAll = useCallback(() => {
    setInput('');
    setOutput('');
    setError(null);
    trackClear('json-minifier');
  }, []);

  return (
    <ToolLayout slug="json-minifier">
      <Card>
        <CardContent className="p-6">
          {/* Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Input JSON
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste your JSON here..."
              className="w-full h-48 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 mb-4">
            <Button onClick={minify} className="gap-2">
              <Package className="w-4 h-4" />
              Minify
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {/* Stats */}
          {output && (
            <div className="mb-4 p-3 bg-surface rounded-lg border border-slate-700">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Original size:</span>
                <span className="text-white font-mono">{input.length} bytes</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-slate-400">Minified size:</span>
                <span className="text-white font-mono">{output.length} bytes</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-slate-400">Reduction:</span>
                <span className="text-emerald-400 font-mono">
                  {Math.round((1 - output.length / input.length) * 100)}%
                </span>
              </div>
            </div>
          )}

          {/* Output */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">
                Output
              </label>
              {output && (
                <div className="flex items-center gap-2">
                  <CopyButton text={output} onCopy={() => trackCopy('json-minifier')} />
                  <DownloadButton content={output} filename="minified.json" onDownload={() => trackDownload('json-minifier')} />
                </div>
              )}
            </div>
            <textarea
              value={error ? '' : output}
              readOnly
              placeholder={error ? '' : 'Minified output will appear here...'}
              className={`w-full h-48 px-4 py-3 bg-surface border rounded-lg font-mono text-sm text-slate-100 resize-y ${
                error ? 'border-red-500' : 'border-slate-700'
              }`}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400 font-medium">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
